// Load the static-hair PLY into a SplatMesh and the manifest JSON.
//
// PLY layout matches packPly.js (standard 3DGS binary) so Spark's PlyReader
// parses it without modification. Gaussians are stored in strand-major order
// (gaussian i = strand i//N, sample i%N) — that ordering is a CONTRACT
// asserted via the manifest's `strand_order: true` flag, because spec_mod
// depends on rebuilding the (S_pad, N_pad) grid from the flat array.

import { SplatMesh, SplatFileType } from '@sparkjsdev/spark';

// cache:'no-store' — the hair PLY is ~150 MB; Chrome's HTTP cache refuses to
// write a file that large and the fetch fails with ERR_CACHE_WRITE_FAILURE,
// which crashed the first load (then a retry succeeded → flickering UX). Not
// caching avoids the write entirely; the file streams straight to memory.
async function fetchBytes(url) {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`fetch ${url}: HTTP ${res.status}`);
  return await res.arrayBuffer();
}

async function fetchJson(url) {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`fetch ${url}: HTTP ${res.status}`);
  return await res.json();
}

/** Returns { mesh, manifest }.
 *  - `mesh` is a SplatMesh with packedSplats ready; caller adds to scene
 *    and sets `.visible`.
 *  - `manifest` is the parsed hair_static.json. */
export async function loadHairPly(baseUrl) {
  const [plyBytes, manifest] = await Promise.all([
    fetchBytes(`${baseUrl}/hair_static.ply`),
    fetchJson(`${baseUrl}/hair_static.json`),
  ]);
  if (!manifest.strand_order) {
    throw new Error('[hair] manifest.strand_order=false — spec_mod requires strand-ordered Gaussians');
  }
  const mesh = new SplatMesh({ fileBytes: plyBytes, fileType: SplatFileType.PLY });
  await mesh.initialized;
  if (mesh.numSplats !== manifest.P) {
    console.warn(`[hair] splat count mismatch: ply has ${mesh.numSplats}, manifest says ${manifest.P}`);
  }
  return { mesh, manifest };
}

/** Fetch const_latent.bin (256*8*8 f32 = 65536 floats) as a Float32Array. */
export async function loadConstLatent(baseUrl) {
  const buf = await fetchBytes(`${baseUrl}/const_latent.bin`);
  return new Float32Array(buf);
}
